import React from "react";
import { Plus, Trash2, X } from "lucide-react";
import { Dropdown } from "../../../../components/Dropdown";
import { Tip } from "../../../../components/ui/tooltip";
import {
  FormField,
  FormInput,
  FormSelect,
  FormTextarea,
  Modal,
  ModalFooterButtons,
} from "../../../../components/Modal";
import type { InterconnectionFormShape } from "../../tabs/InterconnectionsTab";

export interface AddInterconnectionDialogProps {
  open: boolean;
  onClose: () => void;
  handleAddInterconnection: () => void;
  addingInterconnection: boolean;
  interconnectionForm: InterconnectionFormShape;
  setInterconnectionForm: React.Dispatch<
    React.SetStateAction<InterconnectionFormShape>
  >;
}

export function AddInterconnectionDialog({
  open,
  onClose,
  handleAddInterconnection,
  addingInterconnection,
  interconnectionForm,
  setInterconnectionForm,
}: AddInterconnectionDialogProps) {
  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Add Interconnection"
      description="Define a system interconnection or external integration"
      size="lg"
      footer={
        <ModalFooterButtons
          onCancel={onClose}
          onSubmit={handleAddInterconnection}
          submitLabel={addingInterconnection ? "Saving" : "Save"}
          submitDisabled={!interconnectionForm.title || addingInterconnection}
        />
      }
    >
      <FormField label="Title" required>
        <FormInput
          placeholder="e.g. Stripe Payment Gateway"
          value={interconnectionForm.title}
          onChange={(v) =>
            setInterconnectionForm((f) => ({ ...f, title: v }))
          }
        />
      </FormField>
      <FormField label="Description">
        <FormTextarea
          placeholder="Description of the interconnection"
          value={interconnectionForm.description}
          onChange={(v) =>
            setInterconnectionForm((f) => ({ ...f, description: v }))
          }
          rows={2}
        />
      </FormField>
      <div className="grid grid-cols-2 gap-3">
        <FormField label="Direction" required>
          <FormSelect
            options={[
              { value: "incoming", label: "Incoming" },
              { value: "outgoing", label: "Outgoing" },
              { value: "incoming-outgoing", label: "Incoming-Outgoing" },
            ]}
            placeholder="Select direction"
            value={interconnectionForm.direction}
            onChange={(v) =>
              setInterconnectionForm((f) => ({
                ...f,
                direction: v as "incoming" | "outgoing" | "incoming-outgoing",
              }))
            }
          />
        </FormField>
        <FormField label="Remote System Name">
          <FormInput
            placeholder="e.g. Stripe API"
            value={interconnectionForm.remoteSystemName}
            onChange={(v) =>
              setInterconnectionForm((f) => ({
                ...f,
                remoteSystemName: v,
              }))
            }
          />
        </FormField>
      </div>
      <FormField label="Responsible Roles">
        <FormInput
          placeholder="e.g. network-admin, system-owner (comma-separated)"
          value={interconnectionForm.responsibleRoles}
          onChange={(v) =>
            setInterconnectionForm((f) => ({ ...f, responsibleRoles: v }))
          }
        />
      </FormField>

      {/* Protocols */}
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <label className="text-sm font-medium">Protocols</label>
          <button
            type="button"
            className="inline-flex items-center gap-1 text-xs text-primary hover:underline"
            onClick={() =>
              setInterconnectionForm((f) => ({
                ...f,
                protocols: [
                  ...f.protocols,
                  {
                    name: "",
                    portRanges: [{ start: "", end: "", transport: "tcp" }],
                  },
                ],
              }))
            }
          >
            <Plus className="size-3" /> Add More
          </button>
        </div>
        {interconnectionForm.protocols.map((proto, pi) => (
          <div
            key={pi}
            className="rounded-lg border border-border bg-muted/30 p-3 space-y-2"
          >
            <div className="flex items-center gap-2">
              <FormInput
                placeholder="Protocol name (e.g. HTTPS, IPSec)"
                value={proto.name}
                onChange={(v) =>
                  setInterconnectionForm((f) => {
                    const protocols = [...f.protocols];
                    protocols[pi] = { ...protocols[pi], name: v };
                    return { ...f, protocols };
                  })
                }
              />
              <Tip label="Remove Protocol" side="bottom">
                <button
                  type="button"
                  className="p-1 rounded text-muted-foreground hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-500/10 transition-colors flex-shrink-0"
                  onClick={() =>
                    setInterconnectionForm((f) => ({
                      ...f,
                      protocols: f.protocols.filter((_, i) => i !== pi),
                    }))
                  }
                >
                  <Trash2 className="size-3.5" />
                </button>
              </Tip>
            </div>
            <div className="space-y-1.5">
              <div className="flex items-center justify-between">
                <span className="text-xs text-muted-foreground">
                  Port Ranges
                </span>
                <button
                  type="button"
                  className="inline-flex items-center gap-1 text-[10px] text-primary hover:underline"
                  onClick={() =>
                    setInterconnectionForm((f) => {
                      const protocols = [...f.protocols];
                      protocols[pi] = {
                        ...protocols[pi],
                        portRanges: [
                          ...protocols[pi].portRanges,
                          { start: "", end: "", transport: "tcp" },
                        ],
                      };
                      return { ...f, protocols };
                    })
                  }
                >
                  <Plus className="size-2.5" /> Add Port
                </button>
              </div>
              {proto.portRanges.map((pr, pri) => (
                <div key={pri} className="flex items-center gap-2">
                  <input
                    type="number"
                    placeholder="Start"
                    className="w-20 rounded-md border border-input bg-background px-2 py-1 text-xs"
                    value={pr.start}
                    onChange={(e) =>
                      setInterconnectionForm((f) => {
                        const protocols = [...f.protocols];
                        const portRanges = [...protocols[pi].portRanges];
                        portRanges[pri] = {
                          ...portRanges[pri],
                          start: e.target.value,
                        };
                        protocols[pi] = { ...protocols[pi], portRanges };
                        return { ...f, protocols };
                      })
                    }
                  />
                  <span className="text-xs text-muted-foreground">-</span>
                  <input
                    type="number"
                    placeholder="End"
                    className="w-20 rounded-md border border-input bg-background px-2 py-1 text-xs"
                    value={pr.end}
                    onChange={(e) =>
                      setInterconnectionForm((f) => {
                        const protocols = [...f.protocols];
                        const portRanges = [...protocols[pi].portRanges];
                        portRanges[pri] = {
                          ...portRanges[pri],
                          end: e.target.value,
                        };
                        protocols[pi] = { ...protocols[pi], portRanges };
                        return { ...f, protocols };
                      })
                    }
                  />
                  <Dropdown
                    size="sm"
                    variant="form"
                    className="!min-w-[80px]"
                    value={pr.transport}
                    onChange={(v) =>
                      setInterconnectionForm((f) => {
                        const protocols = [...f.protocols];
                        const portRanges = [...protocols[pi].portRanges];
                        portRanges[pri] = {
                          ...portRanges[pri],
                          transport: v,
                        };
                        protocols[pi] = { ...protocols[pi], portRanges };
                        return { ...f, protocols };
                      })
                    }
                    options={[
                      { value: "tcp", label: "TCP" },
                      { value: "udp", label: "UDP" },
                    ]}
                  />
                  <button
                    type="button"
                    className="p-0.5 rounded text-muted-foreground hover:text-red-600 transition-colors flex-shrink-0"
                    onClick={() =>
                      setInterconnectionForm((f) => {
                        const protocols = [...f.protocols];
                        protocols[pi] = {
                          ...protocols[pi],
                          portRanges: protocols[pi].portRanges.filter(
                            (_, i) => i !== pri,
                          ),
                        };
                        return { ...f, protocols };
                      })
                    }
                  >
                    <X className="size-3" />
                  </button>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </Modal>
  );
}
